import { defaultMatchDayDate } from '../domain/matchDay';
import type { DraftMatch } from '../domain/matchSchedule';
import type { PenaltyType } from '../domain/penalties';
import type { Player, PlayerId } from '../api/types';

const SAVING_OFFSET = 100;
const LAST_STEP = 6;
const FIRST_EDIT_STEP = 2;

export interface DraftParticipant extends Player {
  penalties: Partial<Record<PenaltyType, boolean>>;
}

export interface PointsRow {
  _id: PlayerId;
  name: string;
  points: number;
}

export interface PointsState {
  errorMessages: string[];
  /* Set when the wizard is editing a stored Match Day rather than entering a new one. The
     saga PUTs instead of POSTing. */
  editingId: string | null;
  creatorStep: number;
  expandedResults: DraftMatch[];
  dateTime: string;
  users: DraftParticipant[];
  usersPoints: Player[];
  pointsResults: PointsRow[];
  pointsPerPeriod: PointsRow[];
  comment: string;
}

// The reducer sees every dispatched action, so the payload is read positionally per case
// rather than through a union. Ticket 12 replaces this slice with the Match Day intake
// module, where the step machine becomes a discriminated union.
export interface PointsAction {
  type: string;
  [key: string]: unknown;
}

const byName = (a: Player, b: Player): number => {
  if (a.name > b.name) {
    return 1;
  }
  if (a.name < b.name) {
    return -1;
  }
  return 0;
};

export const initialState = (): PointsState => ({
  errorMessages: [],
  editingId: null,
  creatorStep: 0,
  expandedResults: [],
  dateTime: defaultMatchDayDate(),
  users: [],
  usersPoints: [],
  pointsResults: [],
  pointsPerPeriod: [],
  comment: ''
});

export default function points(
  state: PointsState = initialState(),
  action?: PointsAction
): PointsState {
  if (!action) {
    return state;
  }
  switch (action.type) {
    case 'POINTS_ADD_USER_TO_POINTS': {
      const user = action.user as Player;
      return {
        ...state,
        usersPoints: state.usersPoints
          .filter((a) => a._id !== user._id)
          .concat(user)
          .sort(byName)
      };
    }
    case 'POINTS_REMOVE_USER_FROM_POINTS': {
      const user = action.user as Player;
      return {
        ...state,
        usersPoints: state.usersPoints.filter((a) => a._id !== user._id).sort(byName)
      };
    }
    case 'POINTS_USERS_GET_ALL_ACTIVE_SUCCESS':
      return {
        ...state,
        users: (action.users as Player[]).map((user) => ({ ...user, penalties: {} }))
      };
    /* Editing only ever changes results, Penalty Claims and the comment, so the draft is
       loaded whole and the Participant set is fixed. Changing who played would regenerate the
       Matches and discard every score already entered. */
    case 'POINTS_LOAD_DRAFT':
      return {
        ...initialState(),
        editingId: action.editingId as string,
        creatorStep: FIRST_EDIT_STEP,
        dateTime: action.dateTime as string,
        comment: action.comment as string,
        users: action.users as DraftParticipant[],
        usersPoints: action.usersPoints as Player[],
        expandedResults: action.expandedResults as DraftMatch[]
      };
    case 'POINTS_USERS_GET_ALL_ACTIVE_FAIL':
      return { ...state, errorMessages: ['Nie można pobrać użytkowników'] };
    case 'POINTS_SAVE_REQUEST':
      if (state.creatorStep >= LAST_STEP) {
        return { ...state, errorMessages: ['There is no next step'] };
      }
      return { ...state, errorMessages: [], creatorStep: state.creatorStep + 1 + SAVING_OFFSET };
    case 'POINTS_SAVE_SUCCESS':
      return {
        ...state,
        pointsResults: action.results as PointsRow[],
        pointsPerPeriod: action.monthPoints as PointsRow[],
        creatorStep: state.creatorStep - SAVING_OFFSET
      };
    // Without this the wizard stays on the saving sentinel forever and the entered
    // Match Day is unrecoverable.
    case 'POINTS_SAVE_FAIL': {
      const errorMessages = (action.errorMessages as string[] | undefined) ?? ['Nie udało się zapisać'];
      return {
        ...state,
        errorMessages,
        creatorStep: state.creatorStep >= SAVING_OFFSET
          ? state.creatorStep - SAVING_OFFSET - 1
          : state.creatorStep
      };
    }
    case 'POINTS_NEXT_STEP':
      if (state.creatorStep >= LAST_STEP) {
        return { ...state, errorMessages: ['There is no next step'] };
      }
      return { ...state, errorMessages: [], creatorStep: state.creatorStep + 1 };
    case 'POINTS_PREVIOUS_STEP': {
      // Editing starts at the results step: the date and the Participant set are fixed, so
      // there is nothing before it to go back to.
      const floor = state.editingId ? FIRST_EDIT_STEP : 0;
      if (state.creatorStep <= floor) {
        return { ...state, errorMessages: ['There is no previous step'] };
      }
      return { ...state, errorMessages: [], creatorStep: state.creatorStep - 1 };
    }
    case 'POINTS_NEXT_STEP_EXPANDED':
      if (state.creatorStep >= 4) {
        return { ...state, errorMessages: ['There is no next step'] };
      }
      return {
        ...state,
        errorMessages: [],
        creatorStep: state.creatorStep + 1,
        expandedResults: action.expandedResults as DraftMatch[]
      };
    case 'SET_MATCH_SCORE': {
      const matchId = action.matchId as number;
      const player1Score = action.player1Score as number;
      const player2Score = action.player2Score as number;
      return {
        ...state,
        expandedResults: state.expandedResults.map((match) => (
          match._id === matchId
            ? { ...match, player1Score, player2Score, setsNumber: player1Score + player2Score }
            : match
        ))
      };
    }
    case 'POINTS_SET_DATETIME':
      return { ...state, dateTime: action.dateTime as string };
    case 'POINTS_USER_PENALTY': {
      const userId = action.userId as PlayerId;
      const penaltyType = action.penaltyType as PenaltyType;
      const penaltyStatus = action.penaltyStatus as boolean;
      return {
        ...state,
        users: state.users.map((user) => (
          user._id === userId
            ? { ...user, penalties: { ...user.penalties, [penaltyType]: penaltyStatus } }
            : user
        ))
      };
    }
    case 'SAVE_COMMENT':
      return { ...state, comment: action.text as string };
    default:
      return state;
  }
}

export const getPoints = (state: { points: PointsState }): PointsState => state.points;
